import { useEffect, useState } from "react";
import { X, ExternalLink, Link2, ShieldCheck, Share2 } from "lucide-react";
import api from "../../api/client";
import ShareModal from "./ShareModal";

const EVENT_COLOR = {
    ORDER_PLACED: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
    ORDER_MATCHED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    MARGIN_ADDED: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    EARLY_EXIT: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    LIQUIDATION: "text-rose-400 border-rose-500/30 bg-rose-500/10",
    SETTLEMENT: "text-violet-400 border-violet-500/30 bg-violet-500/10",
};

/**
 * Per-swap on-chain attestation modal.
 * Shows every Hyperliquid testnet event for the given swap with a clickable
 * explorer link for each. All attestations are zero-cost (`update_leverage`
 * on the treasury) so the trader sees the full chain footprint with no fee.
 */
export default function OnChainModal({ swapId, coin, settled = false, onClose }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showShare, setShowShare] = useState(false);

    useEffect(() => {
        let alive = true;
        const load = () =>
            api.get(`/swaps/${swapId}/attestations`)
                .then((r) => { if (alive) { setEvents(r.data || []); setLoading(false); } })
                .catch(() => { if (alive) setLoading(false); });
        load();
        const id = setInterval(load, 6000);
        return () => { alive = false; clearInterval(id); };
    }, [swapId]);

    const ok = events.filter((e) => !!e.tx_hash);
    const pending = events.filter((e) => !e.tx_hash);

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center px-4"
            data-testid={`onchain-modal-${swapId}`}
            onClick={onClose}
        >
            <div
                className="w-full max-w-xl bg-[#0A0A0C] border border-[#1F1F24] rounded-sm max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-[#1F1F24] flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">
                            On-chain · Hyperliquid testnet
                        </div>
                        <div className="text-lg font-medium mt-1">
                            {coin} swap · <span className="font-mono text-zinc-400 text-sm">{swapId.slice(0, 10)}…</span>
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400/90 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 rounded-sm">
                            <ShieldCheck className="w-3 h-3" />
                            zero-cost attestations · $0 per event
                        </div>
                    </div>
                    <button
                        data-testid="onchain-modal-close"
                        onClick={onClose}
                        className="text-zinc-500 hover:text-zinc-200 p-1"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {settled && (
                    <div className="px-5 py-3 border-b border-[#1F1F24] flex items-center justify-between gap-3 bg-[#07070A]">
                        <div className="text-[10px] font-mono text-zinc-400">
                            Public proof page · share this trade anywhere
                        </div>
                        <button
                            data-testid="onchain-share-btn"
                            onClick={() => setShowShare(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-400 hover:bg-cyan-300 text-black text-[10px] font-mono uppercase tracking-widest font-semibold rounded-sm"
                        >
                            <Share2 className="w-3 h-3" /> Share trade
                        </button>
                    </div>
                )}

                <div className="overflow-y-auto flex-1">
                    {loading && (
                        <div className="px-5 py-10 text-center text-[11px] font-mono text-zinc-600">
                            loading on-chain attestations…
                        </div>
                    )}
                    {!loading && events.length === 0 && (
                        <div className="px-5 py-10 text-center text-[11px] font-mono text-zinc-600">
                            No on-chain events recorded for this swap yet.
                        </div>
                    )}
                    {events.length > 0 && (
                        <ul className="divide-y divide-[#1F1F24]">
                            {events.map((ev) => {
                                const color = EVENT_COLOR[ev.event_type] || "text-zinc-400 border-zinc-700 bg-zinc-700/10";
                                const clickable = !!ev.explorer_url && !!ev.tx_hash;
                                const Tag = clickable ? "a" : "div";
                                const props = clickable
                                    ? { href: ev.explorer_url, target: "_blank", rel: "noopener noreferrer" }
                                    : {};
                                return (
                                    <li key={ev.id}>
                                        <Tag
                                            {...props}
                                            data-testid={`onchain-modal-row-${ev.id}`}
                                            className={`flex items-center gap-3 px-4 sm:px-5 py-3 ${
                                                clickable ? "hover:bg-[#0F0F12] cursor-pointer" : "opacity-70"
                                            }`}
                                        >
                                            <span className={`shrink-0 text-[9.5px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border ${color}`}>
                                                {ev.event_type.replace("_", " ")}
                                            </span>
                                            <span className="font-mono text-[10px] text-zinc-500 shrink-0 hidden sm:inline">
                                                {new Date(ev.attempted_at).toLocaleString([], { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                            </span>
                                            <div className="flex-1" />
                                            {ev.tx_hash ? (
                                                <span className="inline-flex items-center gap-1 font-mono text-[10px] text-cyan-400/90 shrink-0">
                                                    <Link2 className="w-3 h-3" />
                                                    <span className="truncate max-w-[120px] sm:max-w-[200px]">{ev.tx_hash}</span>
                                                    <ExternalLink className="w-3 h-3" />
                                                </span>
                                            ) : (
                                                <span className="font-mono text-[10px] text-amber-400/80 shrink-0">
                                                    pending
                                                </span>
                                            )}
                                        </Tag>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-[#1F1F24] flex items-center justify-between text-[10px] font-mono text-zinc-500">
                    <span>{ok.length} on-chain · {pending.length} pending</span>
                    <span>Click any row to verify on the explorer ↗</span>
                </div>
            </div>
            {showShare && (
                <ShareModal
                    title={`${coin} BUBE swap · on-chain proof`}
                    text={`${coin} BUBE swap settled on Verslan — ${ok.length} on-chain events verifiable on Hyperliquid testnet.`}
                    url={`${typeof window !== "undefined" ? window.location.origin : ""}/proof/${swapId}`}
                    onClose={() => setShowShare(false)}
                />
            )}
        </div>
    );
}
